/**
 * Settings → Connected apps: what can reach this workspace as an AI app, and
 * the revoke that stops it.
 *
 * The /connect page and the bridge's own messages have sent people here since
 * the device grant shipped, so the page has to exist and has to be honest: a
 * signed-in host and a pasted key are one list, sorted by when each last
 * reached the business; revoke asks once, then bites immediately server-side.
 * No token, key or hash is ever rendered — the server returns none.
 *
 * Post-ratchet file: every user-facing string renders via t() from src/i18n.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { t } from '../../i18n';
import { connectedAppsApi, type ConnectedApp } from '../../services/api/connectedAppsApi';

type Listing =
  | { readonly kind: 'loading' }
  | { readonly kind: 'failed' }
  | { readonly kind: 'ready'; readonly apps: readonly ConnectedApp[] };

type Notice = { readonly tone: 'ok' | 'error'; readonly text: string } | null;

/** How the connection was made, in the words the consent flow used. */
function howConnected(app: ConnectedApp): string {
  if (app.kind === 'api_key') return t('connectedApps.how.apiKey');
  if (app.grantKind === 'device_code') return t('connectedApps.how.device');
  return t('connectedApps.how.browser');
}

function when(iso: string): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? iso : date.toLocaleString();
}

interface RowProps {
  readonly app: ConnectedApp;
  readonly busy: boolean;
  readonly confirming: boolean;
  readonly onAsk: () => void;
  readonly onKeep: () => void;
  readonly onRevoke: () => void;
}

function AppRow({ app, busy, confirming, onAsk, onKeep, onRevoke }: RowProps): JSX.Element {
  return (
    <li
      data-app-id={app.id}
      data-app-kind={app.kind}
      className="rounded-lg border border-gray-200 bg-white p-4"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-gray-900">{app.displayName}</p>
          <p className="text-xs text-gray-600">{howConnected(app)}</p>
        </div>
        {!confirming && (
          <button
            type="button"
            onClick={onAsk}
            disabled={busy}
            className="rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            {t('connectedApps.revoke')}
          </button>
        )}
      </div>

      <ul className="mt-3 space-y-1">
        {app.permissions.map((line) => (
          <li key={line} className="text-xs text-gray-700">
            {line}
          </li>
        ))}
      </ul>

      <p className="mt-3 text-xs text-gray-500">
        {t('connectedApps.connectedOn', { when: when(app.createdAt) })}
        {' · '}
        {app.lastUsedAt
          ? t('connectedApps.lastUsed', { when: when(app.lastUsedAt) })
          : t('connectedApps.neverUsed')}
      </p>

      {confirming && (
        <div role="group" className="mt-3 rounded-md border border-red-200 bg-red-50 p-3">
          <p className="text-sm text-red-800">
            {t('connectedApps.revoke.confirm', { name: app.displayName })}
          </p>
          <div className="mt-2 flex gap-2">
            <button
              type="button"
              onClick={onRevoke}
              disabled={busy}
              className="rounded-md bg-red-700 px-3 py-1.5 text-xs font-medium text-white hover:bg-red-800 disabled:opacity-50"
            >
              {busy ? t('connectedApps.revoke.busy') : t('connectedApps.revoke.yes')}
            </button>
            <button
              type="button"
              onClick={onKeep}
              disabled={busy}
              className="rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              {t('connectedApps.revoke.no')}
            </button>
          </div>
        </div>
      )}
    </li>
  );
}

export default function ConnectedApps(): JSX.Element {
  const [listing, setListing] = useState<Listing>({ kind: 'loading' });
  const [confirmingId, setConfirmingId] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [notice, setNotice] = useState<Notice>(null);

  useEffect(() => {
    let cancelled = false;
    void connectedAppsApi
      .list()
      .then((apps) => {
        if (!cancelled) setListing({ kind: 'ready', apps });
      })
      .catch(() => {
        if (!cancelled) setListing({ kind: 'failed' });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const revoke = async (app: ConnectedApp): Promise<void> => {
    setBusyId(app.id);
    setNotice(null);
    try {
      await connectedAppsApi.revoke(app.id, app.kind);
      setListing((current) =>
        current.kind === 'ready'
          ? { kind: 'ready', apps: current.apps.filter((row) => row.id !== app.id) }
          : current,
      );
      setNotice({ tone: 'ok', text: t('connectedApps.revoked', { name: app.displayName }) });
    } catch {
      setNotice({ tone: 'error', text: t('connectedApps.revoke.failed') });
    } finally {
      setBusyId(null);
      setConfirmingId(null);
    }
  };

  return (
    <div className="mx-auto max-w-2xl p-6">
      <h1 className="text-lg font-semibold text-gray-900">{t('connectedApps.title')}</h1>
      <p className="mt-1 text-sm text-gray-600">{t('connectedApps.subtitle')}</p>

      {notice && (
        <p
          role={notice.tone === 'error' ? 'alert' : 'status'}
          className={`mt-4 text-sm ${notice.tone === 'error' ? 'text-red-700' : 'text-emerald-700'}`}
        >
          {notice.text}
        </p>
      )}

      <div className="mt-6">
        {listing.kind === 'loading' && (
          <p className="text-sm text-gray-500">{t('connectedApps.loading')}</p>
        )}
        {listing.kind === 'failed' && (
          <p role="alert" className="text-sm text-red-700">
            {t('connectedApps.loadFailed')}
          </p>
        )}
        {listing.kind === 'ready' && listing.apps.length === 0 && (
          <p data-testid="connected-apps-empty" className="text-sm text-gray-600">
            {t('connectedApps.empty')}
          </p>
        )}
        {listing.kind === 'ready' && listing.apps.length > 0 && (
          <ul data-testid="connected-apps" className="space-y-3">
            {listing.apps.map((app) => (
              <AppRow
                key={`${app.kind}:${app.id}`}
                app={app}
                busy={busyId === app.id}
                confirming={confirmingId === app.id}
                onAsk={() => setConfirmingId(app.id)}
                onKeep={() => setConfirmingId(null)}
                onRevoke={() => void revoke(app)}
              />
            ))}
          </ul>
        )}
      </div>

      <Link to="/studio" className="mt-8 inline-block text-sm underline">
        {t('assistant.confirm.back')}
      </Link>
    </div>
  );
}
